import { useEffect, useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CheckCircle, Zap, Network, Database, Cloud, Shield, Code, Workflow } from "lucide-react";
import OptimizedImage from "@/components/ImageOptimization";
import cloudPlatformsImage from "@/assets/cloud-platforms-infrastructure.jpg";

interface ServiceModule {
  title: string;
  description: string;
  overview: string;
  features: string[];
  technologies: string[];
  benefits: string[];
  process: string[];
  implementation: {
    strategy: string[];
    configuration: string[];
    optimization: string[];
  };
  useCases?: string[];
}

const mulesoftModules: Record<string, ServiceModule> = {
  "anypoint-platform": {
    title: "Anypoint Platform",
    description: "Complete API-led connectivity platform for designing, building, and managing APIs and integrations.",
    overview: "MuleSoft Anypoint Platform provides a unified integration platform that enables organizations to connect applications, data, and devices across on-premises and cloud environments with API-led connectivity.",
    features: ["API Design & Development", "Integration Development", "API Management", "Runtime Services", "Monitoring & Analytics", "Security & Governance"],
    technologies: ["Anypoint Studio", "Mule Runtime", "API Manager", "Runtime Manager", "CloudHub", "DataWeave"],
    benefits: ["Faster Integration", "Reusable APIs", "Scalable Architecture", "Improved Agility", "Reduced Costs", "Better Visibility"],
    process: ["API Design", "Development", "Testing", "Deployment", "Management", "Optimization"],
    implementation: {
      strategy: ["Integration Strategy", "API-led Architecture", "Governance Framework", "Security Model", "Cloud Strategy"],
      configuration: ["Platform Setup", "API Development", "Integration Flows", "Security Policies", "Monitoring Setup"],
      optimization: ["Performance Tuning", "API Optimization", "Caching Strategy", "Error Handling", "Analytics Enhancement"]
    },
    useCases: ["System Integration", "API Management", "Data Integration", "Legacy Modernization", "Cloud Migration", "B2B Integration"]
  },
  "api-management": {
    title: "API Management",
    description: "Secure, manage, and monitor APIs with comprehensive governance and analytics capabilities.",
    overview: "MuleSoft API Management enables organizations to create, secure, and manage APIs throughout their lifecycle with robust security, rate limiting, and analytics capabilities.",
    features: ["API Gateway", "Security Policies", "Rate Limiting", "Analytics & Monitoring", "Developer Portal", "API Versioning"],
    technologies: ["API Gateway", "OAuth 2.0", "JWT", "Rate Limiting", "API Analytics", "Developer Portal"],
    benefits: ["Secure APIs", "Controlled Access", "Better Visibility", "Developer Experience", "Compliance", "Monetization"],
    process: ["API Publishing", "Policy Configuration", "Developer Onboarding", "Monitoring", "Analytics", "Optimization"],
    implementation: {
      strategy: ["API Strategy", "Security Framework", "Access Control", "Analytics Plan", "Developer Experience"],
      configuration: ["Gateway Setup", "Security Policies", "Rate Limits", "Portal Configuration", "Analytics Tools"],
      optimization: ["Performance Tuning", "Policy Refinement", "Cache Optimization", "Analytics Enhancement", "Developer Feedback"]
    }
  },
  "integration": {
    title: "Integration & Connectivity",
    description: "Connect applications, data, and devices with pre-built connectors and integration patterns.",
    overview: "MuleSoft Integration capabilities enable seamless connectivity between disparate systems using pre-built connectors, templates, and proven integration patterns.",
    features: ["350+ Pre-built Connectors", "Integration Templates", "Data Transformation", "Event-driven Architecture", "Batch Processing", "Error Handling"],
    technologies: ["Mule ESB", "DataWeave", "Connectors", "Integration Patterns", "Message Queues", "Event Streaming"],
    benefits: ["Faster Integration", "Lower Development Time", "Proven Patterns", "Reliable Connectivity", "Scalable Solutions", "Reduced Complexity"],
    process: ["Requirements Analysis", "Integration Design", "Development", "Testing", "Deployment", "Monitoring"],
    implementation: {
      strategy: ["Integration Architecture", "Connector Selection", "Data Mapping", "Error Handling", "Performance Planning"],
      configuration: ["Flow Development", "Connector Configuration", "Transformation Logic", "Error Handling", "Monitoring Setup"],
      optimization: ["Performance Tuning", "Data Optimization", "Cache Implementation", "Parallel Processing", "Resource Management"]
    }
  },
  "cloudhub": {
    title: "CloudHub iPaaS",
    description: "Cloud-based integration platform as a service for deploying and managing integrations.",
    overview: "MuleSoft CloudHub provides a fully managed, multi-tenant cloud integration platform that enables organizations to deploy, run, and manage integrations without infrastructure overhead.",
    features: ["Auto-scaling", "High Availability", "Global Deployment", "Monitoring & Alerts", "Security & Compliance", "Hybrid Deployment"],
    technologies: ["CloudHub Runtime", "VPC", "Load Balancing", "Object Store", "CloudHub API", "Anypoint Monitoring"],
    benefits: ["Zero Infrastructure", "Global Reach", "Automatic Scaling", "Built-in Security", "High Availability", "Cost Efficiency"],
    process: ["Application Design", "CloudHub Deployment", "Configuration", "Monitoring", "Scaling", "Optimization"],
    implementation: {
      strategy: ["Cloud Strategy", "Deployment Model", "Scaling Plan", "Security Framework", "Disaster Recovery"],
      configuration: ["Environment Setup", "Application Deployment", "VPC Configuration", "Load Balancing", "Monitoring Tools"],
      optimization: ["Resource Optimization", "Performance Tuning", "Cost Management", "Auto-scaling Rules", "Regional Distribution"]
    }
  },
  "dataweave": {
    title: "DataWeave Transformation",
    description: "Powerful data transformation language for mapping and transforming data across formats.",
    overview: "DataWeave is MuleSoft's powerful transformation language that enables developers to transform data from any format to any format with a simple, expressive syntax.",
    features: ["Format Transformation", "Data Mapping", "Complex Transformations", "Functions & Operators", "Streaming", "Type System"],
    technologies: ["DataWeave 2.0", "JSON/XML/CSV Support", "Custom Functions", "Type Coercion", "Streaming API", "Pattern Matching"],
    benefits: ["Simplified Transformations", "Type Safety", "Better Performance", "Reusable Functions", "Easy Testing", "Format Agnostic"],
    process: ["Data Analysis", "Mapping Design", "Transformation Development", "Testing", "Optimization", "Deployment"],
    implementation: {
      strategy: ["Transformation Strategy", "Data Standards", "Reusability Plan", "Testing Framework", "Performance Goals"],
      configuration: ["Mapping Development", "Function Libraries", "Type Definitions", "Test Cases", "Error Handling"],
      optimization: ["Performance Tuning", "Memory Optimization", "Streaming Implementation", "Function Optimization", "Best Practices"]
    }
  },
  "rpa": {
    title: "RPA & Automation",
    description: "Robotic process automation for automating repetitive tasks and legacy system integration.",
    overview: "MuleSoft RPA enables organizations to automate repetitive manual tasks, integrate with legacy systems, and create end-to-end automation workflows.",
    features: ["UI Automation", "Process Orchestration", "Bot Management", "Activity Recording", "Exception Handling", "Analytics"],
    technologies: ["RPA Builder", "Bot Runtime", "Process Orchestration", "Activity Library", "Exception Management", "RPA Analytics"],
    benefits: ["Reduced Manual Work", "Faster Processing", "Error Reduction", "Legacy Integration", "Cost Savings", "Scalability"],
    process: ["Process Discovery", "Bot Development", "Testing", "Deployment", "Monitoring", "Optimization"],
    implementation: {
      strategy: ["Process Analysis", "Automation Roadmap", "Bot Architecture", "Change Management", "ROI Framework"],
      configuration: ["Bot Development", "Process Flows", "Exception Rules", "Scheduling", "Monitoring Setup"],
      optimization: ["Bot Performance", "Process Refinement", "Error Reduction", "Parallel Processing", "Continuous Improvement"]
    }
  }
};

const MuleSoftDetail = () => {
  const { moduleId } = useParams();
  const module = moduleId ? mulesoftModules[moduleId] : null;
  const [expandedModules, setExpandedModules] = useState<string[]>([]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [moduleId]);

  const toggleModule = (key: string) => {
    setExpandedModules(prev => 
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  if (moduleId && !module) {
    return <Navigate to="/404" replace />;
  }

  const getIcon = (moduleId: string) => {
    const icons: Record<string, any> = {
      "anypoint-platform": Network,
      "api-management": Shield,
      "integration": Zap,
      "cloudhub": Cloud,
      "dataweave": Code,
      "rpa": Workflow
    };
    return icons[moduleId] || Network;
  };

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <OptimizedImage
            src={cloudPlatformsImage}
            alt="MuleSoft Integration Platform"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/95 to-background/90" />
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl">
            <Badge className="mb-4 bg-secondary/10 text-secondary border-secondary/20">
              Integration Platform
            </Badge>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-foreground">
              {module ? module.title : "MuleSoft Integration Solutions"}
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground mb-8 leading-relaxed">
              {module 
                ? module.description 
                : "Build application networks with API-led connectivity, unlock data across your enterprise, and automate processes with MuleSoft"}
            </p>
          </div>
        </div>
      </section>

      {/* Overview Section */}
      {module && (
        <section className="py-12 md:py-16 bg-muted/30">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-foreground">Overview</h2>
              <p className="text-lg text-muted-foreground leading-relaxed mb-8">
                {module.overview}
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <Card className="border-border/50">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-foreground">
                      <CheckCircle className="h-5 w-5 text-primary" />
                      Key Features
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {module.features.map((feature, index) => (
                        <li key={index} className="flex items-start gap-2 text-muted-foreground">
                          <span className={`${index % 2 === 0 ? 'text-primary shadow-glow' : 'text-secondary shadow-glow-secondary'} mt-1`}>•</span>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>

                <Card className="border-border/50">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-foreground">
                      <Zap className="h-5 w-5 text-secondary" />
                      Benefits
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {module.benefits.map((benefit, index) => (
                        <li key={index} className="flex items-start gap-2 text-muted-foreground">
                          <span className={`${index % 2 === 0 ? 'text-primary shadow-glow' : 'text-secondary shadow-glow-secondary'} mt-1`}>•</span>
                          <span>{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Modules Grid (when no specific module selected) */}
      {!module && (
        <section className="py-12 md:py-16">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center text-foreground">
              MuleSoft Capabilities
            </h2>
            
            <div className="space-y-6 max-w-6xl mx-auto">
              {Object.entries(mulesoftModules).map(([key, mod]) => {
                const Icon = getIcon(key);
                const isExpanded = expandedModules.includes(key);
                return (
                  <Card key={key} className="border-border/50 hover:shadow-lg transition-all duration-300">
                    <Collapsible open={isExpanded} onOpenChange={() => toggleModule(key)}>
                      <CardHeader>
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex items-start gap-3 flex-1">
                            <div className={`p-2 ${Object.keys(mulesoftModules).indexOf(key) % 2 === 0 ? 'bg-primary/10 shadow-glow' : 'bg-secondary/10 shadow-glow-secondary'} rounded-lg`}>
                              <Icon className={`h-6 w-6 ${Object.keys(mulesoftModules).indexOf(key) % 2 === 0 ? 'text-primary shadow-glow' : 'text-secondary shadow-glow-secondary'}`} />
                            </div>
                            <div className="flex-1">
                              <CardTitle className="text-foreground mb-2">{mod.title}</CardTitle>
                              <p className="text-muted-foreground text-sm">{mod.description}</p>
                            </div>
                          </div>
                          <CollapsibleTrigger asChild>
                            <Button variant="outline" size="sm">
                              {isExpanded ? "Show Less" : "Learn More"}
                            </Button>
                          </CollapsibleTrigger>
                        </div>
                      </CardHeader>
                      
                      <CollapsibleContent>
                        <CardContent className="pt-0 space-y-6">
                          {/* Overview */}
                          <div>
                            <h3 className="text-lg font-semibold text-foreground mb-3">Overview</h3>
                            <p className="text-muted-foreground leading-relaxed">{mod.overview}</p>
                          </div>

                          {/* Features and Benefits Grid */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                              <h4 className="text-base font-semibold text-foreground mb-3 flex items-center gap-2">
                                <CheckCircle className="h-4 w-4 text-primary" />
                                Key Features
                              </h4>
                              <ul className="space-y-2">
                                {mod.features.map((feature, idx) => (
                                  <li key={idx} className="flex items-start gap-2 text-sm text-muted-foreground">
                                    <span className={`${idx % 2 === 0 ? 'text-primary shadow-glow' : 'text-secondary shadow-glow-secondary'} mt-1`}>•</span>
                                    <span>{feature}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>

                            <div>
                              <h4 className="text-base font-semibold text-foreground mb-3 flex items-center gap-2">
                                <Zap className="h-4 w-4 text-secondary" />
                                Benefits
                              </h4>
                              <ul className="space-y-2">
                                {mod.benefits.map((benefit, idx) => (
                                  <li key={idx} className="flex items-start gap-2 text-sm text-muted-foreground">
                                    <span className={`${idx % 2 === 0 ? 'text-primary shadow-glow' : 'text-secondary shadow-glow-secondary'} mt-1`}>•</span>
                                    <span>{benefit}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>

                          {/* Implementation Approach */}
                          <div>
                            <h4 className="text-base font-semibold text-foreground mb-4">Implementation Approach</h4>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <div className="bg-muted/30 rounded-lg p-4">
                                <h5 className="text-sm font-semibold text-foreground mb-2">Strategy</h5>
                                <ul className="space-y-1">
                                  {mod.implementation.strategy.map((item, idx) => (
                                    <li key={idx} className="flex items-start gap-2 text-xs text-muted-foreground">
                                      <CheckCircle className={`h-3 w-3 ${idx % 2 === 0 ? 'text-primary shadow-glow' : 'text-secondary shadow-glow-secondary'} mt-0.5 flex-shrink-0`} />
                                      <span>{item}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>

                              <div className="bg-muted/30 rounded-lg p-4">
                                <h5 className="text-sm font-semibold text-foreground mb-2">Configuration</h5>
                                <ul className="space-y-1">
                                  {mod.implementation.configuration.map((item, idx) => (
                                    <li key={idx} className="flex items-start gap-2 text-xs text-muted-foreground">
                                      <CheckCircle className={`h-3 w-3 ${idx % 2 === 0 ? 'text-primary shadow-glow' : 'text-secondary shadow-glow-secondary'} mt-0.5 flex-shrink-0`} />
                                      <span>{item}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>

                              <div className="bg-muted/30 rounded-lg p-4">
                                <h5 className="text-sm font-semibold text-foreground mb-2">Optimization</h5>
                                <ul className="space-y-1">
                                  {mod.implementation.optimization.map((item, idx) => (
                                    <li key={idx} className="flex items-start gap-2 text-xs text-muted-foreground">
                                      <CheckCircle className={`h-3 w-3 ${idx % 2 === 0 ? 'text-primary shadow-glow' : 'text-secondary shadow-glow-secondary'} mt-0.5 flex-shrink-0`} />
                                      <span>{item}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                          </div>

                          {/* Technologies */}
                          <div>
                            <h4 className="text-base font-semibold text-foreground mb-3">Technologies</h4>
                            <div className="flex flex-wrap gap-2">
                              {mod.technologies.map((tech, idx) => (
                                <Badge key={idx} variant="secondary" className="text-xs">
                                  {tech}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        </CardContent>
                      </CollapsibleContent>
                    </Collapsible>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Implementation Section */}
      {module && (
        <section className="py-12 md:py-16">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center text-foreground">
                Implementation Approach
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <Card className="border-border/50">
                  <CardHeader>
                    <CardTitle className="text-foreground">Strategy</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {module.implementation.strategy.map((item, index) => (
                        <li key={index} className="flex items-start gap-2 text-sm text-muted-foreground">
                          <CheckCircle className={`h-4 w-4 ${index % 2 === 0 ? 'text-primary shadow-glow' : 'text-secondary shadow-glow-secondary'} mt-0.5 flex-shrink-0`} />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>

                <Card className="border-border/50">
                  <CardHeader>
                    <CardTitle className="text-foreground">Configuration</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {module.implementation.configuration.map((item, index) => (
                        <li key={index} className="flex items-start gap-2 text-sm text-muted-foreground">
                          <CheckCircle className={`h-4 w-4 ${index % 2 === 0 ? 'text-primary shadow-glow' : 'text-secondary shadow-glow-secondary'} mt-0.5 flex-shrink-0`} />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>

                <Card className="border-border/50">
                  <CardHeader>
                    <CardTitle className="text-foreground">Optimization</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {module.implementation.optimization.map((item, index) => (
                        <li key={index} className="flex items-start gap-2 text-sm text-muted-foreground">
                          <CheckCircle className={`h-4 w-4 ${index % 2 === 0 ? 'text-primary shadow-glow' : 'text-secondary shadow-glow-secondary'} mt-0.5 flex-shrink-0`} />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-12 md:py-16 bg-gradient-to-r from-primary/10 via-secondary/5 to-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-foreground">
              Ready to Connect Your Enterprise?
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Let's discuss how MuleSoft can transform your integration strategy
            </p>
            <Button asChild size="lg" className="bg-primary hover:bg-primary/90">
              <a href="/#contact">Get Started</a>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MuleSoftDetail;
